import { useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Eye, Heart, Calendar, Sparkles, EyeOff, Globe, Play, RotateCcw } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { PremiumAnnouncementReveal } from '@/components/reveal/PremiumAnnouncementReveal';
import { useUserCredits } from '@/hooks/useUserCredits';
import { cn } from '@/lib/utils';
import { getSenderPerspectiveLabel } from '@/lib/senderPerspective';
import { RELATIONSHIP_OPTIONS, type AnnouncementFormData } from '@/types/announcement';
import { getTemplateConfig, type TemplateType } from '@/types/templates';
import { getBackdropPreset } from '@/lib/backdropPresets';
import { PhotoLightbox, EnlargeablePhoto } from '@/components/ui/photo-lightbox';
import { useLanguage } from '@/i18n/LanguageContext';
import { lc } from '@/i18n/copy';
import { TranslationReview } from '@/components/wizard/TranslationReview';
import type { TranslationLanguage } from '@/components/wizard/MessageLanguageSelector';
import { ListenButton } from '@/components/announcement/ListenButton';
import { CinematicVideoPlayer } from '@/components/video/CinematicVideoPlayer';
import { CustomLayoutRenderer } from '@/components/wizard/custom-layouts/CustomLayoutRenderer';
import {
  ForeverInMyHeartBackground,
  ForeverInMyHeartOverlays,
  YourStoryMattersBackground,
  YourStoryMattersOverlays,
  OnceUponATimeBackground,
  OnceUponATimeOverlays,
  TheMomentBackground,
  TheMomentOverlays,
  ReflectionsOnAYearBackground,
  ReflectionsOnAYearOverlays,
  ThankYouBackground,
  ThankYouOverlays,
  GrowthBackground,
  GrowthOverlays,
  OrnamentalDivider,
} from '@/components/templates/TemplateBackgrounds';

const previewCopy = {
  en: { title: 'Preview Your Message', subtitle: "Here's how your message will look.", crushTitle: 'Preview Your Secret Note', crushSubtitle: 'This is how they will see your message.', senderAnon: 'Anonymous', senderFrom: 'From', template: 'Template', note: 'Your message will be reviewed before being published.', noteLabel: 'Note:', crushSender: 'Sent by', watermarkLabel: 'Watermark will appear (free plan)', previewWatermark: 'Preview watermark', footer: "Created with Say It NOW • While They're Still Here", templateNames: { apology: 'Apology', gratitude: 'Gratitude', hard_conversation: 'Hard Conversation', love: 'Love', closure: 'Closure', custom: 'Custom' }, thankYou: 'Thank You', messageOf: 'A message of gratitude for', withAllMyLove: 'With all my love,', fromYour: 'From your', somethingINeedToShare: 'Something I Need to Share', aPersonalMessage: 'A Personal Message', forName: 'For', toName: 'To', withHonestyAndCare: 'With honesty and care', withHeartfeltSincerity: 'With heartfelt sincerity', findingPeace: 'Finding Peace', aMessageOfClosure: 'A Message of Closure', withPeaceAndUnderstanding: 'With peace and understanding', myLovedOne: 'my loved one', aSincereApology: 'A Sincere Apology', imSorry: "I'm Sorry", dearName: 'Dear', withSincereRegret: 'With sincere regret, from your', withAllMyLoveShort: 'With all my love', someoneSpecial: 'Someone Special' },
  es: { title: 'Vista Previa de Tu Mensaje', subtitle: 'Así es como se verá tu mensaje.', crushTitle: 'Vista Previa de Tu Nota Secreta', crushSubtitle: 'Así es como verán tu mensaje.', senderAnon: 'Anónimo', senderFrom: 'De', template: 'Plantilla', note: 'Tu mensaje será revisado antes de ser publicado.', noteLabel: 'Nota:', crushSender: 'Enviado por', watermarkLabel: 'La marca de agua aparecerá (plan gratis)', previewWatermark: 'Vista previa de marca de agua', footer: 'Creado con Say It NOW • Mientras Aún Están Aquí', templateNames: { apology: 'Disculpa', gratitude: 'Gratitud', hard_conversation: 'Conversación Difícil', love: 'Amor', closure: 'Cierre', custom: 'Personalizado' }, thankYou: 'Gracias', messageOf: 'Un mensaje de gratitud para', withAllMyLove: 'Con todo mi amor,', fromYour: 'De tu', somethingINeedToShare: 'Algo que Necesito Compartir', aPersonalMessage: 'Un Mensaje Personal', forName: 'Para', toName: 'Para', withHonestyAndCare: 'Con honestidad y cuidado', withHeartfeltSincerity: 'Con sinceridad de corazón', findingPeace: 'Encontrando Paz', aMessageOfClosure: 'Un Mensaje de Cierre', withPeaceAndUnderstanding: 'Con paz y comprensión', myLovedOne: 'mi ser querido', aSincereApology: 'Una Disculpa Sincera', imSorry: 'Lo Siento', dearName: 'Querido/a', withSincereRegret: 'Con sincero arrepentimiento, de tu', withAllMyLoveShort: 'Con todo mi amor', someoneSpecial: 'Alguien Especial' },
  fr: { title: 'Aperçu de Votre Message', subtitle: 'Voici comment votre message apparaîtra.', crushTitle: 'Aperçu de Votre Note Secrète', crushSubtitle: "Voici comment ils verront votre message.", senderAnon: 'Anonyme', senderFrom: 'De', template: 'Modèle', note: 'Votre message sera examiné avant publication.', noteLabel: 'Note :', crushSender: 'Envoyé par', watermarkLabel: 'Le filigrane apparaîtra (plan gratuit)', previewWatermark: 'Aperçu du filigrane', footer: "Créé avec Say It NOW • Tant Qu'ils Sont Encore Là", templateNames: { apology: 'Excuses', gratitude: 'Gratitude', hard_conversation: 'Conversation Difficile', love: 'Amour', closure: 'Clôture', custom: 'Personnalisé' }, thankYou: 'Merci', messageOf: 'Un message de gratitude pour', withAllMyLove: 'Avec tout mon amour,', fromYour: 'De votre', somethingINeedToShare: 'Quelque Chose à Partager', aPersonalMessage: 'Un Message Personnel', forName: 'Pour', toName: 'À', withHonestyAndCare: 'Avec honnêteté et soin', withHeartfeltSincerity: 'Avec sincérité du cœur', findingPeace: 'Trouver la Paix', aMessageOfClosure: 'Un Message de Clôture', withPeaceAndUnderstanding: 'Avec paix et compréhension', myLovedOne: 'mon être cher', aSincereApology: 'Des Excuses Sincères', imSorry: 'Je Suis Désolé(e)', dearName: 'Cher/Chère', withSincereRegret: 'Avec de sincères regrets, de votre', withAllMyLoveShort: 'Avec tout mon amour', someoneSpecial: 'Quelqu\'un de Spécial' },
  pt: { title: 'Prévia da Sua Mensagem', subtitle: 'Assim ficará sua mensagem.', crushTitle: 'Prévia da Sua Nota Secreta', crushSubtitle: 'É assim que eles verão sua mensagem.', senderAnon: 'Anônimo', senderFrom: 'De', template: 'Modelo', note: 'Sua mensagem será revisada antes de ser publicada.', noteLabel: 'Nota:', crushSender: 'Enviado por', watermarkLabel: 'Marca d\'água aparecerá (plano grátis)', previewWatermark: 'Prévia da marca d\'água', footer: 'Criado com Say It NOW • Enquanto Ainda Estão Aqui', templateNames: { apology: 'Pedido de Desculpas', gratitude: 'Gratidão', hard_conversation: 'Conversa Difícil', love: 'Amor', closure: 'Encerramento', custom: 'Personalizado' }, thankYou: 'Obrigado', messageOf: 'Uma mensagem de gratidão para', withAllMyLove: 'Com todo meu amor,', fromYour: 'Do seu/sua', somethingINeedToShare: 'Algo que Preciso Compartilhar', aPersonalMessage: 'Uma Mensagem Pessoal', forName: 'Para', toName: 'Para', withHonestyAndCare: 'Com honestidade e cuidado', withHeartfeltSincerity: 'Com sinceridade de coração', findingPeace: 'Encontrando Paz', aMessageOfClosure: 'Uma Mensagem de Encerramento', withPeaceAndUnderstanding: 'Com paz e compreensão', myLovedOne: 'meu ente querido', aSincereApology: 'Um Pedido Sincero de Desculpas', imSorry: 'Me Desculpe', dearName: 'Querido/a', withSincereRegret: 'Com sincero arrependimento, do seu/sua', withAllMyLoveShort: 'Com todo meu amor', someoneSpecial: 'Alguém Especial' },
  ar: { title: 'معاينة رسالتك', subtitle: 'هكذا ستبدو رسالتك.', crushTitle: 'معاينة ملاحظتك السرية', crushSubtitle: 'هكذا سيرون رسالتك.', senderAnon: 'مجهول', senderFrom: 'من', template: 'القالب', note: 'ستتم مراجعة رسالتك قبل نشرها.', noteLabel: 'ملاحظة:', crushSender: 'أُرسل من', watermarkLabel: 'ستظهر العلامة المائية (الخطة المجانية)', previewWatermark: 'معاينة العلامة المائية', footer: 'أُنشئ بواسطة Say It NOW • بينما هم لا يزالون هنا', templateNames: { apology: 'اعتذار', gratitude: 'امتنان', hard_conversation: 'محادثة صعبة', love: 'حب', closure: 'إغلاق', custom: 'مخصص' }, thankYou: 'شكراً لك', messageOf: 'رسالة امتنان لـ', withAllMyLove: 'مع كل حبي،', fromYour: 'من', somethingINeedToShare: 'شيء أحتاج لمشاركته', aPersonalMessage: 'رسالة شخصية', forName: 'لـ', toName: 'إلى', withHonestyAndCare: 'بصدق واهتمام', withHeartfeltSincerity: 'بإخلاص من القلب', findingPeace: 'إيجاد السلام', aMessageOfClosure: 'رسالة إغلاق', withPeaceAndUnderstanding: 'مع السلام والتفاهم', myLovedOne: 'حبيبي', aSincereApology: 'اعتذار صادق', imSorry: 'أنا آسف', dearName: 'عزيزي/عزيزتي', withSincereRegret: 'مع أسف صادق، من', withAllMyLoveShort: 'مع كل حبي', someoneSpecial: 'شخص مميز' },
  ja: { title: 'メッセージのプレビュー', subtitle: 'メッセージの見え方です。', crushTitle: '秘密のメモのプレビュー', crushSubtitle: '相手にはこのように表示されます。', senderAnon: '匿名', senderFrom: '差出人', template: 'テンプレート', note: 'メッセージは公開前に審査されます。', noteLabel: '注意:', crushSender: '送信者', watermarkLabel: '透かしが表示されます（無料プラン）', previewWatermark: '透かしプレビュー', footer: 'Say It NOWで作成 • まだここにいるうちに', templateNames: { apology: '謝罪', gratitude: '感謝', hard_conversation: '難しい会話', love: '愛', closure: '区切り', custom: 'カスタム' }, thankYou: 'ありがとう', messageOf: '感謝のメッセージ', withAllMyLove: '心を込めて、', fromYour: 'あなたの', somethingINeedToShare: '伝えたいこと', aPersonalMessage: '個人的なメッセージ', forName: '', toName: '', withHonestyAndCare: '誠実さと思いやりを込めて', withHeartfeltSincerity: '心からの誠意を込めて', findingPeace: '平和を見つける', aMessageOfClosure: '区切りのメッセージ', withPeaceAndUnderstanding: '平和と理解を込めて', myLovedOne: '大切な人', aSincereApology: '心からの謝罪', imSorry: 'ごめんなさい', dearName: '親愛なる', withSincereRegret: '心からの後悔を込めて、あなたの', withAllMyLoveShort: '心を込めて', someoneSpecial: '大切な人' },
  ko: { title: '메시지 미리보기', subtitle: '메시지가 이렇게 보입니다.', crushTitle: '비밀 메모 미리보기', crushSubtitle: '상대방에게 이렇게 보입니다.', senderAnon: '익명', senderFrom: '보낸 사람', template: '템플릿', note: '메시지는 게시 전에 검토됩니다.', noteLabel: '참고:', crushSender: '보낸 사람', watermarkLabel: '워터마크가 표시됩니다 (무료 플랜)', previewWatermark: '워터마크 미리보기', footer: 'Say It NOW로 제작 • 아직 곁에 있을 때', templateNames: { apology: '사과', gratitude: '감사', hard_conversation: '어려운 대화', love: '사랑', closure: '마무리', custom: '맞춤' }, thankYou: '감사합니다', messageOf: '감사의 메시지', withAllMyLove: '온 마음을 담아,', fromYour: '당신의', somethingINeedToShare: '나눌 이야기', aPersonalMessage: '개인 메시지', forName: '', toName: '', withHonestyAndCare: '정직함과 배려로', withHeartfeltSincerity: '진심 어린 성의로', findingPeace: '평화 찾기', aMessageOfClosure: '마무리 메시지', withPeaceAndUnderstanding: '평화와 이해를 담아', myLovedOne: '사랑하는 사람', aSincereApology: '진심 어린 사과', imSorry: '미안합니다', dearName: '사랑하는', withSincereRegret: '진심 어린 후회를 담아, 당신의', withAllMyLoveShort: '온 마음을 담아', someoneSpecial: '소중한 분' },
  id: { title: 'Pratinjau Pesan Anda', subtitle: 'Begini tampilan pesan Anda.', crushTitle: 'Pratinjau Catatan Rahasia Anda', crushSubtitle: 'Begini cara mereka melihat pesan Anda.', senderAnon: 'Anonim', senderFrom: 'Dari', template: 'Template', note: 'Pesan Anda akan ditinjau sebelum diterbitkan.', noteLabel: 'Catatan:', crushSender: 'Dikirim oleh', watermarkLabel: 'Tanda air akan muncul (paket gratis)', previewWatermark: 'Pratinjau tanda air', footer: 'Dibuat dengan Say It NOW • Selagi Mereka Masih Ada', templateNames: { apology: 'Permintaan Maaf', gratitude: 'Rasa Terima Kasih', hard_conversation: 'Percakapan Sulit', love: 'Cinta', closure: 'Penutupan', custom: 'Kustom' }, thankYou: 'Terima Kasih', messageOf: 'Pesan terima kasih untuk', withAllMyLove: 'Dengan segenap cintaku,', fromYour: 'Dari', somethingINeedToShare: 'Sesuatu yang Perlu Kubagikan', aPersonalMessage: 'Pesan Pribadi', forName: 'Untuk', toName: 'Kepada', withHonestyAndCare: 'Dengan kejujuran dan perhatian', withHeartfeltSincerity: 'Dengan ketulusan hati', findingPeace: 'Menemukan Kedamaian', aMessageOfClosure: 'Pesan Penutupan', withPeaceAndUnderstanding: 'Dengan kedamaian dan pengertian', myLovedOne: 'orang tercinta', aSincereApology: 'Permintaan Maaf Tulus', imSorry: 'Maafkan Saya', dearName: 'Untuk', withSincereRegret: 'Dengan penyesalan tulus, dari', withAllMyLoveShort: 'Dengan segenap cintaku', someoneSpecial: 'Seseorang yang Istimewa' },
};

interface PreviewStepProps {
  formData: AnnouncementFormData;
  existingPhotoUrls?: string[];
  updateFormData?: (updates: Partial<AnnouncementFormData>) => void;
}

// Typography preset mappings
function getTypographyClasses(preset: string) {
  switch (preset) {
    case 'modern': return { titleFont: 'font-sans', bodyFont: 'font-sans' };
    case 'handwritten': return { titleFont: 'italic font-serif', bodyFont: 'italic font-serif' };
    case 'classic':
    default: return { titleFont: 'font-serif', bodyFont: 'font-sans' };
  }
}

// Template-specific visual configurations with PRINT-QUALITY backgrounds
// Updated for new 6 emotional intent categories
const TEMPLATE_CONFIGS: Record<TemplateType, {
  name: string;
  Background: React.ComponentType;
  Overlays: React.ComponentType;
  textColors: {
    title: string;
    body: string;
    accent: string;
    muted: string;
  };
  typography: {
    titleFont: string;
    bodyFont: string;
    titleSize: string;
  };
  layout: 'keepsake' | 'reflection' | 'story' | 'moment' | 'editorial';
}> = {
  apology: {
    name: 'Apology',
    Background: ReflectionsOnAYearBackground,
    Overlays: ReflectionsOnAYearOverlays,
    textColors: {
      title: 'text-slate-800 dark:text-slate-100',
      body: 'text-slate-600 dark:text-slate-300',
      accent: 'text-slate-600 dark:text-slate-400',
      muted: 'text-slate-500 dark:text-slate-400',
    },
    typography: {
      titleFont: 'font-heading',
      bodyFont: 'font-sans',
      titleSize: 'text-2xl md:text-3xl',
    },
    layout: 'editorial',
  },
  gratitude: {
    name: 'Gratitude',
    Background: ForeverInMyHeartBackground,
    Overlays: ForeverInMyHeartOverlays,
    textColors: {
      title: 'text-amber-900 dark:text-amber-100',
      body: 'text-amber-800 dark:text-amber-200',
      accent: 'text-amber-700 dark:text-amber-400',
      muted: 'text-amber-600/60 dark:text-amber-400/60',
    },
    typography: {
      titleFont: 'font-serif',
      bodyFont: 'font-sans',
      titleSize: 'text-3xl md:text-4xl',
    },
    layout: 'keepsake',
  },
  hard_conversation: {
    name: 'Hard Conversation',
    Background: YourStoryMattersBackground,
    Overlays: YourStoryMattersOverlays,
    textColors: {
      title: 'text-slate-800 dark:text-slate-100',
      body: 'text-slate-700 dark:text-slate-200',
      accent: 'text-primary',
      muted: 'text-slate-500 dark:text-slate-400',
    },
    typography: {
      titleFont: 'font-serif italic',
      bodyFont: 'font-sans',
      titleSize: 'text-2xl md:text-3xl',
    },
    layout: 'reflection',
  },
  love: {
    name: 'Love',
    Background: TheMomentBackground,
    Overlays: TheMomentOverlays,
    textColors: {
      title: 'text-rose-900 dark:text-rose-100',
      body: 'text-rose-800 dark:text-rose-200',
      accent: 'text-rose-600 dark:text-rose-400',
      muted: 'text-rose-500/60 dark:text-rose-400/60',
    },
    typography: {
      titleFont: 'font-sans font-bold',
      bodyFont: 'font-sans italic',
      titleSize: 'text-xl md:text-2xl',
    },
    layout: 'moment',
  },
  closure: {
    name: 'Closure',
    Background: OnceUponATimeBackground,
    Overlays: OnceUponATimeOverlays,
    textColors: {
      title: 'text-amber-900 dark:text-amber-100',
      body: 'text-amber-800 dark:text-amber-200',
      accent: 'text-amber-700 dark:text-amber-400',
      muted: 'text-amber-600/60 dark:text-amber-400/60',
    },
    typography: {
      titleFont: 'font-serif italic',
      bodyFont: 'font-serif',
      titleSize: 'text-2xl md:text-3xl',
    },
    layout: 'story',
  },
  custom: {
    name: 'Custom',
    Background: YourStoryMattersBackground,
    Overlays: YourStoryMattersOverlays,
    textColors: {
      title: 'text-slate-800 dark:text-slate-100',
      body: 'text-slate-700 dark:text-slate-200',
      accent: 'text-primary',
      muted: 'text-slate-500 dark:text-slate-400',
    },
    typography: {
      titleFont: 'font-serif',
      bodyFont: 'font-sans',
      titleSize: 'text-3xl md:text-4xl',
    },
    layout: 'reflection',
  },
  birthday: {
    name: 'Birthday',
    Background: TheMomentBackground,
    Overlays: TheMomentOverlays,
    textColors: {
      title: 'text-amber-900 dark:text-amber-100',
      body: 'text-amber-800 dark:text-amber-200',
      accent: 'text-amber-700 dark:text-amber-400',
      muted: 'text-amber-600/60 dark:text-amber-400/60',
    },
    typography: {
      titleFont: 'font-sans font-bold',
      bodyFont: 'font-sans',
      titleSize: 'text-3xl md:text-4xl',
    },
    layout: 'moment',
  },
  anniversary: {
    name: 'Anniversary',
    Background: ForeverInMyHeartBackground,
    Overlays: ForeverInMyHeartOverlays,
    textColors: {
      title: 'text-amber-900 dark:text-amber-100',
      body: 'text-amber-800 dark:text-amber-200',
      accent: 'text-amber-700 dark:text-amber-400',
      muted: 'text-amber-600/60 dark:text-amber-400/60',
    },
    typography: {
      titleFont: 'font-serif',
      bodyFont: 'font-serif',
      titleSize: 'text-3xl md:text-4xl',
    },
    layout: 'keepsake',
  },
  celebration: {
    name: 'Celebration',
    Background: YourStoryMattersBackground,
    Overlays: YourStoryMattersOverlays,
    textColors: {
      title: 'text-slate-800 dark:text-slate-100',
      body: 'text-slate-700 dark:text-slate-200',
      accent: 'text-primary',
      muted: 'text-slate-500 dark:text-slate-400',
    },
    typography: {
      titleFont: 'font-sans font-bold',
      bodyFont: 'font-sans',
      titleSize: 'text-3xl md:text-4xl',
    },
    layout: 'moment',
  },
  encouragement: {
    name: 'Encouragement',
    Background: YourStoryMattersBackground,
    Overlays: YourStoryMattersOverlays,
    textColors: {
      title: 'text-emerald-900 dark:text-emerald-100',
      body: 'text-emerald-800 dark:text-emerald-200',
      accent: 'text-cyan-600 dark:text-cyan-400',
      muted: 'text-emerald-500/60 dark:text-emerald-400/60',
    },
    typography: {
      titleFont: 'font-sans font-bold',
      bodyFont: 'font-sans',
      titleSize: 'text-3xl md:text-4xl',
    },
    layout: 'moment',
  },
};

export function PreviewStep({ formData, existingPhotoUrls = [], updateFormData }: PreviewStepProps) {
  const { language } = useLanguage();
  const pc = lc(previewCopy, language);
  const isCrush = formData.relationship === 'crush';
  const allPhotoUrls = [...existingPhotoUrls, ...formData.photoUrls.slice(existingPhotoUrls.length)];
  const config = TEMPLATE_CONFIGS[formData.template] || TEMPLATE_CONFIGS.gratitude;
  const relationship = RELATIONSHIP_OPTIONS.find((r) => r.value === formData.relationship);
  const Background = config.Background;
  const Overlays = config.Overlays;
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [showWatermarkPreview, setShowWatermarkPreview] = useState(false);
  // Auto-show reveal preview when an animation theme is selected
  const hasRevealTheme = !!formData.animationTheme && formData.animationTheme !== 'none';
  const [showRevealPreview, setShowRevealPreview] = useState(hasRevealTheme);
  const [revealKey, setRevealKey] = useState(0);
  const { credits } = useUserCredits();
  const willHaveWatermark = !credits?.has_purchased_bundle;

  // Translation state
  const meta = formData.metadata as Record<string, any> || {};
  const writingLang = (meta.writingLanguage || 'en') as TranslationLanguage;
  const recipientLang = (meta.recipientLanguage || 'en') as TranslationLanguage;
  const needsTranslation = writingLang !== recipientLang;
  const [translationDone, setTranslationDone] = useState(!!meta.translatedMessage);
  const sendVersion = meta.sendVersion || 'original';

  // Show TranslationReview if languages differ and user hasn't chosen yet
  if (needsTranslation && !translationDone) {
    return (
      <TranslationReview
        originalMessage={formData.message}
        writingLanguage={writingLang}
        recipientLanguage={recipientLang}
        template={formData.template}
        onUseTranslated={(translated) => {
          if (updateFormData) {
            updateFormData({
              metadata: {
                ...meta,
                originalMessage: formData.message,
                translatedMessage: translated,
                sendVersion: 'translated',
              } as any,
              message: translated,
            });
          }
          setTranslationDone(true);
        }}
        onKeepOriginal={() => {
          if (updateFormData) {
            updateFormData({
              metadata: {
                ...meta,
                sendVersion: 'original',
              } as any,
            });
          }
          setTranslationDone(true);
        }}
        onEditOriginal={() => {
          // User wants to go back and edit — they'll use the wizard back button
          setTranslationDone(true);
        }}
      />
    );
  }

  // Resolve sender display for crush
  const getSenderDisplay = () => {
    const mode = formData.metadata?.senderDisplayMode;
    if (mode === 'confession') return '🔒 ' + pc.senderAnon;
    if (mode === 'anonymous' || !mode) return pc.senderAnon;
    if (mode === 'nickname') return formData.metadata?.senderNickname || pc.senderAnon;
    return pc.senderFrom; // 'named' - will use profile name at publish
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-6"
    >
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-primary/10 text-primary mb-4">
          <Eye className="h-8 w-8" />
        </div>
        <h2 className="text-2xl font-bold text-foreground mb-2">
          {isCrush ? pc.crushTitle : pc.title}
        </h2>
        <p className="text-muted-foreground max-w-md mx-auto">
          {isCrush ? pc.crushSubtitle : pc.subtitle}
        </p>
      </div>

      {/* Sender identity indicator for crush */}
      {isCrush && (
        <div className="flex justify-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/5 border border-primary/20 text-sm text-primary">
            <span>{pc.crushSender}: <strong>{getSenderDisplay()}</strong></span>
          </div>
        </div>
      )}

      {/* Template + Reveal indicators */}
      <div className="flex flex-wrap justify-center gap-3">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-muted text-muted-foreground text-sm">
          <Sparkles className="h-4 w-4" />
          <span>{pc.template}: <strong>{(pc as any).templateNames?.[formData.template] || config.name}</strong></span>
        </div>
        {!showRevealPreview ? (
          <Button
            variant="outline"
            size="sm"
            className="rounded-full gap-2"
            onClick={() => { setShowRevealPreview(true); setRevealKey(k => k + 1); }}
          >
            <Play className="h-3.5 w-3.5" />
            Preview Reveal Animation
          </Button>
        ) : (
          <Button
            variant="outline"
            size="sm"
            className="rounded-full gap-2"
            onClick={() => setRevealKey(k => k + 1)}
          >
            <RotateCcw className="h-3.5 w-3.5" />
            Replay
          </Button>
        )}
      </div>

      {/* Watermark preview toggle */}
      <div className="max-w-2xl mx-auto flex items-center justify-end gap-2">
        {showRevealPreview && (
          <button
            onClick={() => setShowRevealPreview(false)}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors mr-auto"
          >
            ← Back to static preview
          </button>
        )}
        <Label htmlFor="watermark-toggle" className="text-xs text-muted-foreground cursor-pointer">
          {willHaveWatermark ? (pc as any).watermarkLabel || 'Watermark will appear (free plan)' : (pc as any).previewWatermark || 'Preview watermark'}
        </Label>
        <Switch
          id="watermark-toggle"
          checked={showWatermarkPreview || willHaveWatermark}
          onCheckedChange={setShowWatermarkPreview}
          disabled={willHaveWatermark}
        />
      </div>

      {/* Preview: Reveal Animation or Static Template */}
      <div className="max-w-2xl mx-auto">
        {showRevealPreview ? (
          /* Live reveal animation preview — exactly what recipient sees */
          <motion.div
            key={revealKey}
            className="rounded-3xl overflow-hidden shadow-2xl"
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.1 }}
          >
            <PremiumAnnouncementReveal
              animationTheme={formData.animationTheme || 'elegant_floral'}
              recipientName={formData.recipientName || 'Recipient'}
              relationship={formData.relationship || 'friend'}
              message={formData.message}
              template={formData.template}
              photoUrls={allPhotoUrls.length > 0 ? allPhotoUrls : undefined}
              senderLabel={isCrush ? getSenderDisplay() : undefined}
            />
          </motion.div>
        ) : (
          /* Static template preview */
          <motion.div
            className="rounded-3xl overflow-hidden shadow-2xl relative isolate min-h-[500px]"
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            {/* Watermark overlay in preview */}
            {(showWatermarkPreview || willHaveWatermark) && (
              <div className="absolute inset-0 z-20 pointer-events-none overflow-hidden rounded-3xl" aria-hidden="true">
                <div
                  className="absolute inset-[-50%] flex flex-wrap items-center justify-center gap-16"
                  style={{ transform: 'rotate(-30deg)', opacity: 0.07 }}
                >
                  {Array.from({ length: 24 }, (_, i) => (
                    <div key={i} className="flex items-center gap-2 shrink-0">
                      <span className="font-heading font-bold text-foreground text-lg whitespace-nowrap tracking-tight">
                        Say It <span className="text-primary">NOW</span>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Rich background layers */}
            {formData.template === 'custom' ? (
              /* Custom template uses the full CustomLayoutRenderer */
              <CustomLayoutRenderer
                formData={formData}
                allPhotoUrls={allPhotoUrls}
                className="min-h-[500px]"
              />
            ) : (
              <>
                <Background />
                <Overlays />
                <div className="relative z-10 p-8 md:p-12">
                  {config.layout === 'keepsake' && (
                    <KeepsakeLayout config={config} formData={formData} allPhotoUrls={allPhotoUrls} relationship={relationship} copy={pc as any} />
                  )}
                  {config.layout === 'reflection' && (
                    <ReflectionLayout config={config} formData={formData} allPhotoUrls={allPhotoUrls} relationship={relationship} copy={pc as any} />
                  )}
                  {config.layout === 'story' && (
                    <StoryLayout config={config} formData={formData} allPhotoUrls={allPhotoUrls} relationship={relationship} copy={pc as any} />
                  )}
                  {config.layout === 'moment' && (
                    <MomentLayout config={config} formData={formData} allPhotoUrls={allPhotoUrls} relationship={relationship} copy={pc as any} />
                  )}
                  {config.layout === 'editorial' && (
                    <EditorialLayout config={config} formData={formData} allPhotoUrls={allPhotoUrls} relationship={relationship} copy={pc as any} />
                  )}
                </div>
              </>
            )}

            {/* Footer — skip for custom (handled inside CustomLayoutRenderer) */}
            {formData.template !== 'custom' && (
              <div className="relative z-10 px-6 py-4 text-center">
                <p className={cn('text-xs', config.textColors.muted)}>
                  {(pc as any).footer || "Created with Say It NOW • While They're Still Here"}
                </p>
              </div>
            )}
          </motion.div>
        )}
      </div>

      {/* Photo Lightbox */}
      {allPhotoUrls.length > 0 && (
        <PhotoLightbox
          photos={allPhotoUrls}
          initialIndex={lightboxIndex}
          open={lightboxOpen}
          onOpenChange={setLightboxOpen}
        />
      )}

      {/* Listen to your message */}
      <div className="flex justify-center">
        <ListenButton message={formData.message} />
      </div>

      {/* Attached video message — sender preview */}
      {(() => {
        const vUrl = (formData.metadata as any)?.videoUrl as string | undefined;
        if (!vUrl) return null;
        console.log('[PreviewStep] video attachment present', { vUrl });
        return (
          <div className="max-w-2xl mx-auto">
            <CinematicVideoPlayer src={vUrl} label="Video Message Attached" />
          </div>
        );
      })()}

      {/* Custom template note - editing happens at step 4 */}
      {formData.template === 'custom' && (
        <div className="max-w-md mx-auto text-center">
          <p className="text-sm text-muted-foreground">
            ✨ Go back to step 4 to customize your layout, colors, fonts, photos, and decorations.
          </p>
        </div>
      )}

      {/* Status note */}
      <div className="max-w-md mx-auto text-center">
        <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
          <p className="text-sm text-amber-800 dark:text-amber-200">
            <strong>{pc.noteLabel}</strong> {pc.note}
          </p>
        </div>
      </div>
    </motion.div>
  );
}

// ============================================
// LAYOUT COMPONENTS
// ============================================

interface LayoutCopy {
  thankYou: string; messageOf: string; withAllMyLove: string; fromYour: string;
  somethingINeedToShare: string; aPersonalMessage: string; forName: string; toName: string;
  withHonestyAndCare: string; withHeartfeltSincerity: string; findingPeace: string;
  aMessageOfClosure: string; withPeaceAndUnderstanding: string; myLovedOne: string;
  aSincereApology: string; imSorry: string; dearName: string; withSincereRegret: string;
  withAllMyLoveShort: string; someoneSpecial: string;
}

interface LayoutProps {
  config: typeof TEMPLATE_CONFIGS[TemplateType];
  formData: AnnouncementFormData;
  allPhotoUrls: string[];
  relationship?: { label: string; emoji: string; value?: string };
  copy: LayoutCopy;
}

// KEEPSAKE LAYOUT - "Forever in My Heart"
function KeepsakeLayout({ config, formData, allPhotoUrls, relationship, copy: c }: LayoutProps) {
  const messageLines = (formData.message || '').split('\n');
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  return (
    <>
    <div className="text-center py-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="mb-8"
      >
        <h3 className={cn(config.typography.titleFont, config.typography.titleSize, config.textColors.title, 'tracking-wide')}>
          {c.thankYou}
        </h3>
        <p className={cn('text-sm mt-2', config.textColors.accent)}>
          {c.messageOf} {formData.recipientName || c.someoneSpecial}
        </p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, scaleX: 0 }}
        animate={{ opacity: 1, scaleX: 1 }}
        transition={{ delay: 0.4 }}
        className="flex items-center justify-center gap-3 mb-10"
      >
        <div className="w-12 h-px bg-current opacity-30" style={{ color: 'inherit' }} />
        <Heart className={cn('h-4 w-4', config.textColors.accent)} fill="currentColor" />
        <div className="w-12 h-px bg-current opacity-30" style={{ color: 'inherit' }} />
      </motion.div>

      {allPhotoUrls.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="flex justify-center gap-4 mb-10 flex-wrap"
        >
          {allPhotoUrls.map((url, index) => (
            <EnlargeablePhoto
              key={index}
              src={url}
              alt={`Photo ${index + 1}`}
              className="w-24 h-24 md:w-32 md:h-32 rounded-lg overflow-hidden shadow-xl border-4 border-white/80 dark:border-slate-700/80"
              onClick={() => { setLightboxIndex(index); setLightboxOpen(true); }}
            />
          ))}
        </motion.div>
      )}

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.6 }}
        className="max-w-md mx-auto mb-12 space-y-4"
      >
        {messageLines.map((line, index) => (
          <p 
            key={index} 
            className={cn(
              config.typography.bodyFont,
              'text-lg leading-relaxed',
              config.textColors.body,
              line.trim() === '' ? 'h-4' : ''
            )}
          >
            {line}
          </p>
        ))}
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.7 }}
        className="space-y-2"
      >
        <p className={cn('text-lg font-medium', config.textColors.accent)}>
          {c.withAllMyLove}
        </p>
        <p className={cn('text-xl font-serif italic', config.textColors.accent)}>
          — {c.fromYour} {getSenderPerspectiveLabel(relationship?.value ?? relationship?.label)}
        </p>
      </motion.div>
    </div>
    {allPhotoUrls.length > 0 && (
      <PhotoLightbox photos={allPhotoUrls} initialIndex={lightboxIndex} open={lightboxOpen} onOpenChange={setLightboxOpen} />
    )}
    </>
  );
}

// REFLECTION LAYOUT - Hard Conversation / Custom
function ReflectionLayout({ config, formData, allPhotoUrls, relationship, copy: c }: LayoutProps) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const isCustom = formData.template === 'custom';
  const cs = formData.customSettings;
  const typo = isCustom ? getTypographyClasses(cs.typographyPreset) : null;
  const align = isCustom ? (cs.textAlignment === 'left' ? 'text-left' : 'text-center') : 'text-center';
  const titleWeight = isCustom && !cs.titleBold ? 'font-normal' : '';
  const titleFontClass = typo ? typo.titleFont : config.typography.titleFont;
  const bodyFontClass = typo ? typo.bodyFont : config.typography.bodyFont;
  const bgPhotoUrl = isCustom && cs.backgroundPhotoIndex !== null ? allPhotoUrls[cs.backgroundPhotoIndex] : null;
  const inlinePhotos = bgPhotoUrl
    ? allPhotoUrls.filter((_, i) => i !== cs.backgroundPhotoIndex).slice(0, 3)
    : allPhotoUrls.slice(0, 3);

  return (
    <>
    {bgPhotoUrl && (
      <div className="absolute inset-0 z-0 overflow-hidden">
        {(() => { const bp = getBackdropPreset(cs.typographyPreset); return (<>
          <img src={bgPhotoUrl} alt="" className={cn("w-full h-full object-cover", bp.imageBlur, bp.imageOpacity)} />
          <div className={cn("absolute inset-0 bg-gradient-to-b", cs.autoContrast ? bp.overlayOn : bp.overlayOff)} />
        </>); })()}
      </div>
    )}
    <div className={cn("relative z-[2] py-6", align)}>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
        <p className={cn('text-sm uppercase tracking-widest mb-4', config.textColors.accent)}>
          {isCustom ? c.aPersonalMessage : c.somethingINeedToShare}
        </p>
        <h3 className={cn(titleFontClass, titleWeight, config.typography.titleSize, config.textColors.title, 'mb-2')}>
          {isCustom ? `${c.forName} ${formData.recipientName || c.someoneSpecial}` : `${c.toName} ${formData.recipientName || c.someoneSpecial}`}
        </h3>
        <p className={cn('text-sm capitalize mb-8', config.textColors.muted)}>
          {isCustom ? `${c.fromYour} ${getSenderPerspectiveLabel(relationship?.value ?? relationship?.label)}` : c.withHonestyAndCare}
        </p>
      </motion.div>

      {inlinePhotos.length > 0 && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }} className={cn("flex gap-4 mb-8", align === 'text-center' ? 'justify-center' : 'justify-start')}>
          {inlinePhotos.map((url, index) => (
            <EnlargeablePhoto key={index} src={url} alt={`Photo ${index + 1}`}
              className="w-20 h-20 md:w-24 md:h-24 rounded-full overflow-hidden shadow-lg border-2 border-white/60"
              onClick={() => { setLightboxIndex(index); setLightboxOpen(true); }} />
          ))}
        </motion.div>
      )}

      <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}
        className={cn(bodyFontClass, 'text-lg leading-relaxed whitespace-pre-wrap max-w-lg mb-8', config.textColors.body, align === 'text-center' ? 'mx-auto' : '')}>
        {formData.message || ''}
      </motion.p>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}
        className={cn('flex items-center gap-2', config.textColors.accent, align === 'text-center' ? 'justify-center' : '')}>
        <Heart className="h-4 w-4" fill="currentColor" />
        <span className="text-sm font-medium">{isCustom ? c.withHeartfeltSincerity : c.withHonestyAndCare}</span>
      </motion.div>
    </div>
    {allPhotoUrls.length > 0 && <PhotoLightbox photos={allPhotoUrls} initialIndex={lightboxIndex} open={lightboxOpen} onOpenChange={setLightboxOpen} />}
    </>
  );
}
// STORY LAYOUT - Closure
function StoryLayout({ config, formData, allPhotoUrls, relationship, copy: c }: LayoutProps) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  return (
    <>
    <div className="py-6">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="text-center mb-8">
        <p className={cn('text-xs uppercase tracking-[0.3em] mb-4', config.textColors.muted)}>{c.findingPeace}</p>
        <h3 className={cn(config.typography.titleFont, config.typography.titleSize, config.textColors.title)}>{c.aMessageOfClosure}</h3>
      </motion.div>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }} className={cn('flex justify-center mb-8', config.textColors.accent)}>
        <OrnamentalDivider />
      </motion.div>
      {allPhotoUrls.length > 0 && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.45 }} className="flex justify-center mb-8">
          <div className="bg-white dark:bg-slate-800 p-2 pb-6 shadow-xl rotate-1">
            <EnlargeablePhoto src={allPhotoUrls[0]} alt="Memory" className="w-32 h-32 md:w-40 md:h-40"
              onClick={() => { setLightboxIndex(0); setLightboxOpen(true); }} />
          </div>
        </motion.div>
      )}
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }} className="max-w-lg mx-auto text-center mb-8">
        <p className={cn('text-sm mb-4', config.textColors.muted)}>{c.forName} {formData.recipientName || c.someoneSpecial}, {c.myLovedOne}</p>
        <p className={cn(config.typography.bodyFont, 'text-lg leading-loose whitespace-pre-wrap', config.textColors.body)}>
          {formData.message || ''}
        </p>
      </motion.div>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }} className="text-center">
        <div className="w-16 h-px bg-current opacity-30 mx-auto mb-4" />
        <p className={cn('text-sm italic', config.textColors.accent)}>{c.withPeaceAndUnderstanding}</p>
      </motion.div>
    </div>
    {allPhotoUrls.length > 0 && <PhotoLightbox photos={[allPhotoUrls[0]]} initialIndex={0} open={lightboxOpen} onOpenChange={setLightboxOpen} />}
    </>
  );
}

// MOMENT LAYOUT - Love / Encouragement
function MomentLayout({ config, formData, allPhotoUrls, relationship, copy: c }: LayoutProps) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const isEncouragement = formData.template === 'encouragement';
  const chibiUrl = (formData.metadata as any)?.chibiUrl;

  return (
    <>
    <div className="text-center py-8 flex flex-col items-center justify-center min-h-[400px]">
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.7 }} className="mb-8">
        {isEncouragement && (
          <p className="text-xs uppercase tracking-[0.25em] text-emerald-600 font-semibold mb-2">You Got This 💪</p>
        )}
        <p className={cn('text-xs uppercase tracking-widest mb-6', config.textColors.muted)}>{c.forName} {formData.recipientName || c.someoneSpecial}</p>
      </motion.div>

      {/* Show chibi for encouragement if available */}
      {isEncouragement && chibiUrl && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ type: 'spring', damping: 15, delay: 0.35 }}
          className="mb-6 max-w-[220px]"
        >
          <img src={chibiUrl} alt="Mini chibi encouragement" className="w-full h-auto rounded-xl shadow-lg" />
        </motion.div>
      )}

      {allPhotoUrls.length > 0 && (
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.4 }}
          className="w-40 h-40 md:w-48 md:h-48 rounded-2xl overflow-hidden shadow-2xl mb-10 border-4 border-white/60">
          <EnlargeablePhoto src={allPhotoUrls[0]} alt="The moment" className="w-full h-full"
            onClick={() => setLightboxOpen(true)} />
        </motion.div>
      )}
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }} className="max-w-md mx-auto">
        <p className={cn(config.typography.bodyFont, 'text-xl md:text-2xl leading-relaxed', config.textColors.body)}>
          "{formData.message || ''}"
        </p>
      </motion.div>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}
        className={cn('flex items-center gap-2 mt-10', config.textColors.accent)}>
        {isEncouragement ? (
          <>
            <Sparkles className="h-4 w-4" />
            <span className="text-sm">Keep Going • You're Amazing ✨</span>
          </>
        ) : (
          <>
            <Heart className="h-4 w-4" fill="currentColor" />
            <span className="text-sm">{c.withAllMyLoveShort}</span>
          </>
        )}
      </motion.div>
    </div>
    {allPhotoUrls.length > 0 && <PhotoLightbox photos={allPhotoUrls} initialIndex={0} open={lightboxOpen} onOpenChange={setLightboxOpen} />}
    </>
  );
}

// EDITORIAL LAYOUT - Apology
function EditorialLayout({ config, formData, allPhotoUrls, relationship, copy: c }: LayoutProps) {
  const paragraphs = (formData.message || '').split('\n\n');
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const photos = allPhotoUrls.slice(0, 3);

  return (
    <>
    <div className="py-6">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="text-center mb-8">
        <p className={cn('text-xs uppercase tracking-[0.25em] mb-2', config.textColors.accent)}>{c.aSincereApology}</p>
        <h3 className={cn(config.typography.titleFont, config.typography.titleSize, config.textColors.title, 'mb-2')}>{c.imSorry}</h3>
        <p className={cn('text-sm', config.textColors.muted)}>{c.dearName} {formData.recipientName || c.someoneSpecial}</p>
      </motion.div>
      <div className="w-24 h-px bg-current opacity-20 mx-auto mb-8" />
      {photos.length > 0 && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }} className="flex justify-center gap-3 mb-8">
          {photos.map((url, index) => (
            <EnlargeablePhoto key={index} src={url} alt={`Photo ${index + 1}`}
              className="w-16 h-16 md:w-20 md:h-20 rounded-sm overflow-hidden shadow-md grayscale-[20%]"
              onClick={() => { setLightboxIndex(index); setLightboxOpen(true); }} />
          ))}
        </motion.div>
      )}
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }} className="max-w-lg mx-auto space-y-6">
        {paragraphs.map((para, i) => (
          <div key={i} className="text-center">
            <p className={cn(config.typography.bodyFont, 'text-base leading-relaxed', config.textColors.body)}>{para}</p>
            {i < paragraphs.length - 1 && (
              <div className="flex justify-center gap-1.5 mt-4">
                <div className="w-1 h-1 rounded-full bg-current opacity-30" />
                <div className="w-1 h-1 rounded-full bg-current opacity-40" />
                <div className="w-1 h-1 rounded-full bg-current opacity-30" />
              </div>
            )}
          </div>
        ))}
      </motion.div>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }} className="text-center mt-8">
        <div className="w-16 h-px bg-current opacity-20 mx-auto mb-4" />
        <p className={cn('text-sm', config.textColors.accent)}>
          {c.withSincereRegret} {getSenderPerspectiveLabel(relationship?.value ?? relationship?.label)}
        </p>
      </motion.div>
    </div>
    {photos.length > 0 && <PhotoLightbox photos={photos} initialIndex={lightboxIndex} open={lightboxOpen} onOpenChange={setLightboxOpen} />}
    </>
  );
}
